import { useMemo } from 'react';

import { Trans } from '@lingui/react/macro';
import { cn } from '@repo/videa-components/src/lib/utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/videa-components/src/ui/select';

import { isWebImageType } from '@/modules/annotator/annotation-utils/annotation-utils';
import { useLatestImagesV2 } from '@/react-query/useLatestImagesv2';

import { VideaTypography } from '../../../common-components/elements/VideaTypography/VideaTypography';
import {
  type AdjustmentVersion,
  useGetSharpenedImage,
} from '../../../react-query/useGetSharpenedImages';

import { FILTER_VERSIONS, getFilterLabel } from './filterConstants';

interface FilterComparisonPreviewProps {
  leftFilter: AdjustmentVersion;
  rightFilter: AdjustmentVersion;
  imageId: string;
  onLeftFilterChange: (filter: AdjustmentVersion) => void;
  onRightFilterChange: (filter: AdjustmentVersion) => void;
  visitId: number;
  patientId: string;
}

const DisplayImage = ({
  image,
  filter,
  loading,
  error,
  isLeftFilter,
}: {
  image?: string;
  filter: AdjustmentVersion;
  loading: boolean;
  error: boolean;
  isLeftFilter: boolean;
}) => {
  return loading ? (
    <div
      className={cn(
        'vai-absolute vai-inset-0 vai-flex vai-items-center vai-justify-center vai-text-sm',
        !isLeftFilter && 'vai-text-blue-600 vai-bg-blue-50',
      )}
    >
      <Trans>Loading...</Trans>
    </div>
  ) : error ? (
    <div
      className={cn(
        'vai-absolute vai-inset-0 vai-flex vai-items-center vai-justify-center vai-text-sm vai-text-red-600',
        !isLeftFilter && ' vai-bg-blue-50',
      )}
    >
      <Trans>Error loading</Trans>
    </div>
  ) : image ? (
    <>
      <img
        src={image}
        alt={getFilterLabel(filter)}
        className={cn(
          'vai-w-full vai-h-full vai-object-contain',
          !isLeftFilter && 'vai-bg-blue-50',
        )}
      />
      <div
        className={cn(
          'vai-absolute vai-bottom-0 vai-left-0 vai-right-0 vai-text-white vai-text-xs vai-py-1 vai-px-2 vai-text-center vai-font-semibold',
          !isLeftFilter && 'vai-bg-blue-600',
          isLeftFilter && 'vai-bg-black vai-opacity-50',
        )}
      >
        {getFilterLabel(filter)} {!isLeftFilter && '✓'}
      </div>
    </>
  ) : (
    <div
      className={cn(
        'vai-absolute vai-inset-0 vai-flex vai-items-center vai-justify-center vai-text-sm',
        !isLeftFilter && 'vai-bg-blue-50 vai-text-blue-600',
        isLeftFilter && 'vai-bg-black vai-opacity-50 vai-text-white',
      )}
    >
      {getFilterLabel(filter)}
      <Trans> Preview</Trans>
    </div>
  );
};

export const FilterComparisonPreview = ({
  leftFilter,
  rightFilter,
  imageId,
  onLeftFilterChange,
  onRightFilterChange,
  visitId,
  patientId,
}: FilterComparisonPreviewProps) => {
  const leftImageQuery = useGetSharpenedImage({
    imageId: imageId || null,
    adjustmentVersion: leftFilter,
  });

  const rightImageQuery = useGetSharpenedImage({
    imageId: imageId || null,
    adjustmentVersion: rightFilter,
  });

  const {
    data: images,
    isLoading: isLoadingImages,
    isError: isErrorImages,
  } = useLatestImagesV2({
    videaPatientId: patientId,
    estimatedVisitId: visitId.toString(),
  });

  const originalImage = useMemo(() => {
    const originalImageInfo = images?.analyses?.find(
      (image) => image.imageId === Number(imageId),
    );
    return isWebImageType(originalImageInfo?.path)
      ? originalImageInfo?.path
      : originalImageInfo?.originalPath;
  }, [images, imageId]);

  return (
    <div className="vai-flex vai-flex-col vai-gap-3">
      <VideaTypography variant="labelSmall">
        <Trans>Step 3: Compare Filters Side-by-Side</Trans>
      </VideaTypography>
      <div className="vai-grid vai-grid-cols-2 vai-gap-4">
        {/* Left Side - Comparison */}
        <div className="vai-flex vai-flex-col vai-gap-2">
          <div className="vai-flex vai-items-center vai-gap-2 vai-h-7">
            <VideaTypography
              variant="labelExtrasmall"
              className="vai-text-gray-600"
            >
              <Trans>Compare With</Trans>
            </VideaTypography>
          </div>

          <Select
            value={leftFilter}
            onValueChange={(v: AdjustmentVersion) => onLeftFilterChange(v)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FILTER_VERSIONS.map((filter) => (
                <SelectItem key={filter.version} value={filter.version}>
                  {filter.description}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="vai-relative vai-aspect-video vai-bg-gray-200 vai-rounded vai-overflow-hidden">
            {leftFilter === 'original' ? (
              <DisplayImage
                image={originalImage}
                filter={leftFilter}
                loading={isLoadingImages}
                error={isErrorImages}
                isLeftFilter={true}
              />
            ) : (
              <DisplayImage
                image={leftImageQuery.data?.s3Path}
                filter={leftFilter}
                loading={leftImageQuery.isLoading}
                error={leftImageQuery.isError}
                isLeftFilter={true}
              />
            )}
          </div>
        </div>

        {/* Right Side - Your Selection */}
        <div className="vai-flex vai-flex-col vai-gap-2">
          <div className="vai-flex vai-items-center vai-gap-2 vai-h-7">
            <VideaTypography
              variant="labelExtrasmall"
              className="vai-text-blue-600 vai-font-semibold"
            >
              <Trans>Your Selection</Trans>
            </VideaTypography>
            <span className="vai-px-2 vai-py-0.5 vai-rounded vai-text-xs vai-font-semibold vai-bg-blue-600 vai-text-white">
              <Trans>Will Save</Trans>
            </span>
          </div>
          <Select
            value={rightFilter}
            onValueChange={(v: AdjustmentVersion) => onRightFilterChange(v)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FILTER_VERSIONS.map((filter) => (
                <SelectItem key={filter.version} value={filter.version}>
                  {filter.description}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="vai-relative vai-aspect-video vai-rounded vai-overflow-hidden vai-border-2 vai-border-blue-600">
            {rightFilter === 'original' ? (
              <DisplayImage
                image={originalImage}
                filter={rightFilter}
                loading={isLoadingImages}
                error={isErrorImages}
                isLeftFilter={false}
              />
            ) : (
              <DisplayImage
                image={rightImageQuery.data?.s3Path}
                filter={rightFilter}
                loading={rightImageQuery.isLoading}
                error={rightImageQuery.isError}
                isLeftFilter={false}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
